/**
 * OpenAI-compatible Chat Completions API types
 *
 * These types represent the standard format for multi-modal content
 * in chat completion requests, compatible with OpenAI's API spec.
 */

import { z } from "zod";

import { openEnum } from "./openEnum";

/** ========================================
 * Message Role
 * ======================================== */

export const MessageRoleSchema = z.enum(["system", "user", "assistant"]);
export type MessageRole = z.infer<typeof MessageRoleSchema>;

/** ========================================
 * Multi-Modal Content Types
 * ======================================== */

export const TextContentSchema = z.object({
  type: z.literal("text"),
  text: z.string(),
});
export type TextContent = z.infer<typeof TextContentSchema>;

export const ImageContentSchema = z.object({
  type: z.literal("image_url"),
  image_url: z.object({
    url: z.string(),
  }),
});
export type ImageContent = z.infer<typeof ImageContentSchema>;

export const VideoContentSchema = z.object({
  type: z.literal("video_url"),
  video_url: z.object({
    url: z.string(),
  }),
});
export type VideoContent = z.infer<typeof VideoContentSchema>;

export const InputAudioFormatSchema = z.enum(["wav", "mp3", "flac"]);
export type InputAudioFormat = z.infer<typeof InputAudioFormatSchema>;

/** Inline audio, compatible with the OpenAI `input_audio` part: raw file bytes as base64, no `data:` prefix. */
export const InputAudioContentSchema = z.object({
  type: z.literal("input_audio"),
  input_audio: z.object({
    data: z.string(),
    format: InputAudioFormatSchema,
  }),
});
export type InputAudioContent = z.infer<typeof InputAudioContentSchema>;

export const AudioContentSchema = z.object({
  type: z.literal("audio_url"),
  audio_url: z.object({
    url: z.string(),
  }),
});
export type AudioContent = z.infer<typeof AudioContentSchema>;

export const MultiModalContentSchema = z.union([
  TextContentSchema,
  ImageContentSchema,
  VideoContentSchema,
  InputAudioContentSchema,
  AudioContentSchema,
]);
export type MultiModalContent = z.infer<typeof MultiModalContentSchema>;

/** ========================================
 * Message Types
 * ======================================== */

export const MessageContentSchema = z.union([z.string(), z.array(MultiModalContentSchema)]);
export type MessageContent = z.infer<typeof MessageContentSchema>;

export const ChatMessageSchema = z.object({
  role: MessageRoleSchema,
  content: MessageContentSchema,
});
export type ChatMessage = z.infer<typeof ChatMessageSchema>;

/** ========================================
 * System Hint/Vision Config Types
 * ======================================== */
export const AnnotationFormatSchema = z.enum(["box", "point", "polygon", "clip"]);
export type AnnotationFormat = z.infer<typeof AnnotationFormatSchema>;

const SystemHintSchema = z.enum(
  AnnotationFormatSchema.options.map((o) => o.toUpperCase()) as [
    Uppercase<AnnotationFormat>,
    ...Uppercase<AnnotationFormat>[],
  ],
);
export type SystemHint = z.infer<typeof SystemHintSchema>;

export const ChatCompletionKwargsSchema = z.object({
  system_hints: z.array(SystemHintSchema).optional(),
  enable_audio_in_video: z.boolean().optional(),
});
export type ChatCompletionKwargs = z.infer<typeof ChatCompletionKwargsSchema>;

export const VisionConfigSchema = z.object({
  annotation_format: AnnotationFormatSchema.nullable().optional(),
  /** Analyze the audio track of each video in the request. Off by default; audio parts are analyzed regardless. */
  enable_audio_in_video: z.boolean().optional(),
});
export type VisionConfig = z.infer<typeof VisionConfigSchema>;

/**
 * How much the model reasons before it answers. Whether the model reasons at all is decided here
 * too: `none` turns reasoning off, any other tier turns it on.
 */
export const ReasoningEffortSchema = z.enum(["none", "minimal", "low", "medium", "high"]);
export type ReasoningEffort = z.infer<typeof ReasoningEffortSchema>;

/** The tier that turns reasoning off. */
export const NO_REASONING_EFFORT: ReasoningEffort = "none";
/** The tier a binary thinking toggle maps to while it is on; the toggle has no tiers of its own. */
export const THINKING_REASONING_EFFORT: ReasoningEffort = "high";

/** The `reasoning_effort` for a binary thinking flag: a tier when on, `none` when off, absent when unset. */
export function reasoningEffortForThinking(enableThinking: boolean | null | undefined): ReasoningEffort | undefined {
  if (enableThinking === null || enableThinking === undefined) return undefined;
  return enableThinking ? THINKING_REASONING_EFFORT : NO_REASONING_EFFORT;
}

/** ========================================
 * Built-in Tool Types
 * ======================================== */

export const BuiltInToolSchema = z.object({
  type: z.string().min(1),
});
export type BuiltInTool = z.infer<typeof BuiltInToolSchema>;

/** ========================================
 * Request Types
 * ======================================== */

export const ChatCompletionRequestSchema = z.object({
  model: z.string(),
  messages: z.array(ChatMessageSchema),
  temperature: z.number().optional(),
  top_p: z.number().optional(),
  stream: z.boolean().optional(),
  max_tokens: z.number().int().min(0).optional(),
  max_completion_tokens: z.number().int().min(0).optional(),
  presence_penalty: z.number().optional(),
  frequency_penalty: z.number().optional(),
  stream_options: z
    .object({
      include_usage: z.boolean().optional(),
    })
    .optional(),
  vision_config: VisionConfigSchema.optional(),
  chat_template_kwargs: ChatCompletionKwargsSchema.optional(),
  reasoning_effort: ReasoningEffortSchema.optional(),
  tools: z.array(BuiltInToolSchema).optional(),
});
export type ChatCompletionRequest = z.infer<typeof ChatCompletionRequestSchema>;

/** ========================================
 * Response Types
 * ======================================== */

// Open: a model may finish for a reason this build does not name, and one new value must not reject the stream.
export const FinishReasonSchema = openEnum(["stop", "length", "tool_error", "tool_limit"]);
export type FinishReason = z.infer<typeof FinishReasonSchema>;

/**
 * Breakdown of `prompt_tokens`. Every field is optional: the gateway reports `audio_tokens`, while
 * models the demo calls directly (`nebius` / `modal` providers) may report only `cached_tokens`, or
 * nothing at all.
 */
export const PromptTokensDetailsSchema = z
  .object({
    /** Prompt tokens from audio parts and, with `enable_audio_in_video`, video soundtracks. `0` means no audio was analyzed. */
    audio_tokens: z.number().nonnegative().optional(),
    cached_tokens: z.number().nonnegative().optional(),
  })
  .passthrough();
export type PromptTokensDetails = z.infer<typeof PromptTokensDetailsSchema>;

export const ChatCompletionUsageSchema = z
  .object({
    prompt_tokens: z.number().nonnegative(),
    completion_tokens: z.number().nonnegative(),
    total_tokens: z.number().nonnegative(),
    prompt_tokens_details: PromptTokensDetailsSchema.nullish(),
  })
  .passthrough();
export type ChatCompletionUsage = z.infer<typeof ChatCompletionUsageSchema>;

/** ========================================
 * Streaming Response Types
 * ======================================== */

export const ChatCompletionDeltaSchema = z
  .object({
    role: z.union([openEnum(MessageRoleSchema.options), z.null()]).optional(),
    content: z.union([z.string(), z.null()]).optional(),
    reasoning_content: z.union([z.string(), z.null()]).optional(),
  })
  .passthrough();
export type ChatCompletionDelta = z.infer<typeof ChatCompletionDeltaSchema>;

export const ChatCompletionStreamChoiceSchema = z
  .object({
    index: z.number().nonnegative().optional(),
    delta: ChatCompletionDeltaSchema,
    finish_reason: z.union([FinishReasonSchema, z.null()]).optional(),
  })
  .passthrough();
export type ChatCompletionStreamChoice = z.infer<typeof ChatCompletionStreamChoiceSchema>;

export const ChatCompletionStreamResponseSchema = z
  .object({
    id: z.string(),
    object: z.literal("chat.completion.chunk"),
    created: z.number(),
    model: z.string(),
    choices: z.array(ChatCompletionStreamChoiceSchema),
    usage: ChatCompletionUsageSchema.nullable().optional(),
  })
  .passthrough();
export type ChatCompletionStreamResponse = z.infer<typeof ChatCompletionStreamResponseSchema>;

export const ChatCompletionStreamDataSchema = z.union([ChatCompletionStreamResponseSchema, z.literal("[DONE]")]);
export type ChatCompletionStreamData = z.infer<typeof ChatCompletionStreamDataSchema>;

/** ========================================
 * Error Types
 * ======================================== */

/** OpenAI error body: an HTTP error response, or the SSE event a stream sends when it fails partway through. */
export const ChatCompletionErrorSchema = z.object({
  error: z.object({
    message: z.string(),
    type: z.string().nullable().optional(),
    param: z.string().nullable().optional(),
    code: z.string().nullable().optional(),
  }),
});
export type ChatCompletionError = z.infer<typeof ChatCompletionErrorSchema>;
